import { useCallback } from 'react'
import { ContractInterface } from 'ethers'

// Types
import { WalletReturnType } from 'hooks/useWallet'

// Hooks
import { useContract } from 'hooks/useContract/useContract'

// Utils
import { toBigNumber, toNormalNumber } from 'utils/bigNumber'

/**
 * Hook used to interact with ERC20 tokens.
 * @param state Metamask state.
 * @param abi Contract abi.
 * @param address Contract address.
 * @param decimals Token decimals.
 */
export const useERC20 = (
  state: WalletReturnType,
  abi: ContractInterface,
  address: string,
  decimals: number,
  network?: string
) => {
  const { callGetter, callSetter, setEvent, cleanEvents } = useContract(
    state,
    abi,
    address,
    network
  )

  const approve = useCallback(
    (addressToApprove: string, amount: number) => {
      return callSetter('approve', [
        addressToApprove,
        toBigNumber(amount, decimals),
      ])
    },
    [callSetter, decimals]
  )

  const allowance = useCallback(
    (owner: string, spender: string) => {
      return callGetter('allowance', [owner, spender]).then((allowance) =>
        toNormalNumber(allowance as number, decimals)
      )
    },
    [callGetter, decimals]
  )

  const balanceOf = useCallback(
    (address: string) => {
      return callGetter('balanceOf', [address]).then((balance) =>
        toNormalNumber(balance as number, decimals)
      )
    },
    [callGetter, decimals]
  )

  const burnAndBridge = useCallback(
    (amount: number, poktAddress: string) => {
        return callSetter('burnAndBridge', [
            toBigNumber(amount * 1000000, 0),
            poktAddress,
        ])
    },
    [callSetter, decimals]
  )

   return { balanceOf, approve, allowance, burnAndBridge, setEvent, cleanEvents }
}
